import React, { useState, useRef, useEffect } from "react";
import PrimaryButton from "../../elements/primary-button";
import BodyText from "../../elements/body-text";
import Heading from "../../elements/heading";
import PropTypes from "prop-types";
import PdpOption from "../../react-components/pdp-option";
import InfoModal from "../../components/InfoModal";
import InputModal from "../../react-components/input-modal";
import NotifyMeModal from "../../react-components/notify-me-modal";
import IconLink from "../../elements/icon-link";
import LoadingSpinner from "../../elements/loading-spinner";
import "./style.scss";
import { scrollToSmoothByPosition, scrollToSmoothPDP } from "../../hooks/scrollToSmooth.js";
import { IS_MOBILE } from "../../constants";

const PdpOptionList = ({ options, userChoices, buttonEvents, disclaimerMtM, disclaimerBaC, locationInfo, isOutOfStock, outOfStockInfo, notifyMeInfo, chatButton, acceptButton, onPlanChanged, scrollOffset, onSelectCallback, canvas, scene, isFetchingNewData, isServicePackage = false }) => {
  const [optionList, setOptionList] = useState([]);
  const [selectedOption, setSelectedOption] = useState(0);
  const [modalContent, setModalContent] = useState({});
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showLocationCaption, setShowLocationCaption] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(null);

  const refs = useRef([]);
  const gridRef = useRef();
  const openInfoModal = content => {
    setModalContent(content);
    setShowInfoModal(true);
  };

  // Filter enabled choices when component mounts
  useEffect(() => {
    filterOptions(userChoices);
  }, []);

  const filterOptions = newPlan => {
    const checkPrerequisites = reqs => {
      if (reqs) {
        for (let i = 0; i < reqs.length; i++) {
          if (!newPlan.find(choice => choice && choice.id === reqs[i])) return false;
        }
      }
      return true;
    };
    const newOptions = options.filter(option => !option.prerequisites || checkPrerequisites(option.prerequisites));
    setOptionList(newOptions);
    return newOptions;
  };

  useEffect(() => {
    // This will trigger setOptionList, which will trigger the below effect
    filterOptions(userChoices);

    userChoices.forEach(choice => {
      if (buttonEvents && choice.id in buttonEvents) {
        buttonEvents[choice.id]();
      }
    });
  }, [userChoices, options]);

  useEffect(() => {
    for (let i = 0; i < optionList.length; i++) {
      let option = optionList[i];

      // Check all buttons for each option
      let n = 0;
      for (let j = 0; j < option?.buttons?.length; j++) {
        let button = option.buttons[j];

        if (userChoices.find(choice => choice && choice.id == button.id)) {
          n++;
        }
      }
      for (let j = 0; j < option?.checkboxes?.length; j++) {
        let button = option.checkboxes[j];
        if (userChoices.find(choice => choice && choice.id == button.id && choice.isRequired && choice?.checked)) {
          n++;
        }
      }
      // If at least one option has been selected, enable next step
      if (n === 0) {
        setSelectedOption(i);
        return;
      }
    }
    setSelectedOption(optionList.length);
  }, [optionList]);

  const onAccept = () => {
    setTimeout(() => {
      if (!gridRef.current) return null;
      const m2m = userChoices.find(element => element.id === "month-to-month");
      if (m2m) return;
      const elem = gridRef.current.children[gridRef.current?.children.length - 1];
      var top = elem.offsetTop + elem.offsetHeight - document.getElementById("pdp-pricetag")?.clientHeight + 20;
      // var ua = navigator.userAgent;
      // var isMobile = /Android|webOS|iPhone|iPad|iPod/i.test(ua);
      if (IS_MOBILE) {
        window.scrollTo({
          behavior: "smooth",
          top: top
        });
      } else {
        scrollToSmoothByPosition(top);
      }
    }, 300);
    if (acceptButton?.callback) {
      acceptButton.callback(userChoices);
    }
  };
  const checkboxRecovery = (plans, newOptions) => {
    newOptions.forEach(newItem => {
      newItem.checkboxes &&
        newItem.checkboxes.forEach(checkbox => {
          checkbox.check = false;
        });
    });
  };
  const onOptionSelected = (index, buttonId, buttonOptions, type, nextIndex) => {
    let plan = [...userChoices];
    const selected = buttonOptions.find(opt => opt.id === buttonId);

    if (type === "checkbox") {
      plan = plan.filter(item => !item?.isCheckbox);
      // Checkboxes are special because we can have multiple choices selected at once.
      let newCheckboxes = [...buttonOptions];
      let thisCheckbox = newCheckboxes.find(item => item?.id === buttonId);
      thisCheckbox.checked = !thisCheckbox.checked;

      // Only include affirmative choices
      newCheckboxes = newCheckboxes.filter(item => item?.checked);

      plan = [...plan, ...newCheckboxes];
    } else {
      plan[index - 1] = selected;
    }

    /** Measurement */
    let dataLayer = window.dataLayer || [];

    if (selected?.id === "month-to-month" || selected?.id === "buy-a-car") {
      let member_level = "";

      switch (selected.id) {
        case "month-to-month":
          member_level = "Level1";
          break;
        case "buy-a-car":
          member_level = "Level3";
          break;
      }
      dataLayer.push({
        event: "memberChoice",
        eventCategory: "membership",
        eventAction: "level chosen",
        eventLabel: member_level
      });
    }
    if (selected?.id.includes("color")) {
      dataLayer.push({
        event: "colorChoice",
        eventCategory: "membership car configuration",
        eventAction: "color",
        eventLabel: selected.skuAttrDefaultValue.toLowerCase()
      });
    }
    if (selected?.id.includes("engine-")) {
      dataLayer.push({
        event: "engineChoice",
        eventCategory: "membership car configuration",
        eventAction: "engine",
        eventLabel: selected.skuAttrDefaultValue.toLowerCase()
      });
    }
    if (selected?.id.includes("-towbar")) {
      dataLayer.push({
        event: "towbarChoice",
        eventCategory: "membership car configuration",
        eventAction: "towbar",
        eventLabel: selected.id ? (selected.id.includes("no") ? "no" : "yes") : ""
      });
    }
    if (selected?.id.includes("-winter")) {
      dataLayer.push({
        event: "winterWheelsChoice",
        eventCategory: "membership car configuration",
        eventAction: "winter wheels",
        eventLabel: selected.id.toLowerCase()
      });
    }
    if (selected?.id.includes("storage")) {
      dataLayer.push({
        event: "serviceChoice",
        eventCategory: "membership car configuration",
        eventAction: "shift & storage service",
        eventLabel: selected.id ? (selected.id.includes("no") ? "no" : "yes") : ""
      });
    }
    if (selected?.id.includes("service-plan")) {
      dataLayer.push({
        event: "buyCarRefineLoanServicePackage",
        eventCategory: "buy a car refine loan",
        eventAction: "service package",
        eventLabel: selected.id
      });
    }
    if (selected?.id.includes("mileage-opt-")) {
      dataLayer.push({
        event: "buyCarRefineLoanServicePackageMilageOpt",
        eventCategory: "buy a car refine loan",
        eventAction: "service package milage option",
        eventLabel: selected.id
      });
    }

    const newOptions = filterOptions(plan);
    if (type !== "checkbox") {
      plan = plan.slice(0, newOptions?.length);
      checkboxRecovery(plan, newOptions);
    }
    if (onPlanChanged) {
      onPlanChanged(plan);
    }

    // if (selectedOption === optionList?.length && acceptButton?.callback) {
    //   acceptButton.callback(plan);
    // }

    selected.buttonCallback && selected.buttonCallback(scene, canvas);
    if (onSelectCallback) {
      onSelectCallback(plan);
    }

    if (index < newOptions.length && optionList[index - 1].autoScroll) {
      /* Timeout is necessary to get the correct scroll position, as PDP buttons with an USP list will expand when clicked on mobile */
      waitForElement("pdp-option-" + index, function() {
        let id = "pdp-option-" + nextIndex;
        let nextOption = document.getElementById(id);
        // var ua = navigator.userAgent;
        // var isMobile = /Android|webOS|iPhone|iPad|iPod/i.test(ua);

        if (IS_MOBILE) {
          setTimeout(() => {
            window.scrollTo({
              behavior: "smooth",
              top: refs.current[index]?.offsetTop - scrollOffset() - 60 // extra 60 for the header
            });
          }, 300);
        } else {
          scrollToSmoothByPosition(refs.current[index]?.offsetTop - scrollOffset() - 60, false);
        }
      });
    }
    if (!refs.current[index] && nextIndex === optionList.length - 1) {
      //scroll to button-container
      let nextOption = isServicePackage ? document.getElementById("button-container-1") : document.getElementById("button-container");
      scrollToSmoothByPosition(nextOption?.offsetTop - scrollOffset() - 80, false);

      //scrollToSmoothPDP(nextOption, true);
    }
  };

  const onOptionChecked = (index, checkboxes, checkbox, value, nextIndex) => {
    let checkboxesNew = [];
    let newIndex = index;
    checkboxes.forEach((item, i) => {
      if (item?.id === checkbox?.id) {
        item.check = value;
      }
      checkboxesNew.push(item);
    });
    onOptionSelected(newIndex, checkbox?.id, checkboxesNew, "checkbox", nextIndex);
  };
  const updateLocation = data => {
    setShowLocationCaption(true);
  };
  const callback = () => {
    let $chat = document.querySelector(".LPMcontainer");
    if ($chat && $chat.click) {
      $chat.click();
    }
  };
  const waitForElement = (elementId, callBack) => {
    window.setTimeout(function() {
      var element = document.getElementById(elementId);
      if (element) {
        callBack(elementId, element);
      } else {
        waitForElement(elementId, callBack);
      }
    }, 500);
  };

  return (
    <div className="pdp-option-list">
      <div className="pdp-options grid" ref={gridRef}>
        {optionList.map((option, index) => {
          return (
            <PdpOption
              key={option.id ? option.id : index}
              {...{
                ...option,
                ...{
                  id: index.toString(),
                  enabled: index <= selectedOption,
                  reference: el => (refs.current[index] = el),
                  onInfoClicked: () => openInfoModal(option.info),
                  onSelected: id => onOptionSelected(index + 1, id, option?.buttons, null, index),
                  onCheckboxChecked: (value, checkbox) => onOptionChecked(index + 1, option?.checkboxes, checkbox, value, index)
                }
              }}
            />
          );
        })}
        {isFetchingNewData && (
          <div className="spinner-container">
            <LoadingSpinner />
          </div>
        )}
        <section className="button-container" id={isServicePackage ? "button-container-1" : "button-container"}>
          {isOutOfStock ? (
            <>
              <Heading type={{ fields: { phrase: { value: "h4" } } }} className="out-of-stock-heading" html={outOfStockInfo.heading} />
              <BodyText html={outOfStockInfo.body} />
              <div className="chat-button">
                <PrimaryButton
                  {...{
                    ...chatButton,
                    link: { value: { ...chatButton?.link?.value, href: null, linktype: null } }
                  }}
                  callback={callback}
                />
                {/*<IconLink {...notifyMeInfo.button} callback={() => setShowNotifyModal(true)} />*/}
                <IconLink {...notifyMeInfo?.button} />
              </div>
            </>
          ) : (
            <>
              {userChoices[0]?.id === "month-to-month" ? <BodyText html={disclaimerMtM} className="disclaimer" /> : <BodyText html={disclaimerBaC} className="disclaimer" />}
              {/*{locationInfo && (*/}
              {/*  <div className="location-info">*/}
              {/*    <BodyText html={locationInfo.body} />*/}
              {/*    <IconLink {...locationInfo.button} callback={() => setShowLocationModal(true)} />*/}
              {/*    {showLocationCaption && locationInfo.caption && <BodyText html={locationInfo.caption} className="location-caption" />}*/}
              {/*  </div>*/}
              {/*)}*/}
              <PrimaryButton
                {...{
                  ...acceptButton,
                  link: { value: { ...acceptButton?.link?.value, href: null, linktype: null } }
                }}
                isDisabled={selectedOption !== optionList.length || isFetchingNewData}
                callback={onAccept}
              />
            </>
          )}
        </section>
      </div>
      <InfoModal {...modalContent} showModal={showInfoModal} setShowModal={setShowInfoModal} />
      {locationInfo && <InputModal {...locationInfo.modal} id={"change-location-modal"} showModal={showLocationModal} setShowModal={setShowLocationModal} callback={updateLocation} />}
      {notifyMeInfo && <NotifyMeModal {...notifyMeInfo.modal} showModal={showNotifyModal} setShowModal={setShowNotifyModal} />}
    </div>
  );
};

PdpOptionList.propTypes = {
  // options: PropTypes.arrayOf(
  //   PropTypes.shape({
  //     heading: PropTypes.object,
  //     info: PropTypes.object,
  //     buttons: PropTypes.array,
  //     prerequisites: PropTypes.arrayOf(PropTypes.string)
  //   })
  // ),
  buttonEvents: PropTypes.object,
  disclaimerMtM: PropTypes.string,
  disclaimerBaC: PropTypes.string,
  locationInfo: PropTypes.shape({
    body: PropTypes.string,
    button: PropTypes.object,
    modal: PropTypes.object
  }),
  isOutOfStock: PropTypes.bool,
  outOfStockInfo: PropTypes.shape({
    heading: PropTypes.object,
    body: PropTypes.string
  }),
  notifyMeLink: PropTypes.object,
  chatButton: PropTypes.object,
  acceptButton: PropTypes.object,
  onPlanChanged: PropTypes.func,
  scrollOffset: PropTypes.func,
  onSelectCallback: PropTypes.func
};

PdpOptionList.defaultProps = {
  scrollOffset: 0
};

export default PdpOptionList;
